import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ledgerApi, mydataError, ACCOUNT_TYPES } from "@/api";
import { useLang } from "@/i18n";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const EMPTY = { code: "", name: "", type: "asset" };

export const ChartOfAccounts = ({ onOpenAccount }) => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => { ledgerApi.accounts().then(setRows).catch(() => toast.error(t("error"))); }, [t]);
  useEffect(load, [load]);

  const openDialog = (acc) => { setEditing(acc); setForm(acc ? { code: acc.code, name: acc.name, type: acc.type } : EMPTY); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return toast.error(t("required"));
    try {
      await (editing ? ledgerApi.updateAccount(editing.code, form) : ledgerApi.createAccount(form));
      toast.success(t("account_saved")); setOpen(false); load();
    } catch (err) { toast.error(mydataError(err, t)); }
  };
  const remove = async () => {
    try { await ledgerApi.deleteAccount(deleting.code); toast.success(t("account_deleted")); load(); }
    catch (err) { toast.error(mydataError(err, t)); } finally { setDeleting(null); }
  };

  const filtered = rows.filter((a) => !q || a.code.startsWith(q) || a.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="chart-of-accounts-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="max-w-sm" data-testid="coa-search-input" />
        <Button onClick={() => openDialog(null)} className="bg-blue-800 hover:bg-blue-700" data-testid="create-account-button"><Plus size={16} className="mr-1" />{t("new_account")}</Button>
      </div>
      <div className="erp-card overflow-hidden">
        <table className="w-full text-sm erp-table">
          <thead><tr className="text-left"><th className="px-4 py-3">{t("account_code")}</th><th className="px-4 py-3">{t("account_name")}</th><th className="px-4 py-3">{t("account_type")}</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((a) => (
              <tr key={a.code} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`coa-row-${a.code}`}>
                <td className="px-4 py-2 font-mono" style={{ paddingLeft: `${16 + a.code.split(".").length * 12 - 12}px` }}>{a.code}</td>
                <td className="px-4 py-2">{a.name}{a.system && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{t("system_acc")}</span>}</td>
                <td className="px-4 py-2 text-slate-500">{t(`acc_${a.type}`)}</td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => onOpenAccount(a.code)} data-testid={`coa-ledger-${a.code}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><BookOpen size={15} /></button>
                    <button onClick={() => openDialog(a)} data-testid={`coa-edit-${a.code}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Pencil size={15} /></button>
                    {!a.system && <button onClick={() => setDeleting(a)} data-testid={`coa-delete-${a.code}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit_account") : t("new_account")}</DialogTitle><DialogDescription className="sr-only">{t("account")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">{t("account_code")}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={editing?.system} className="font-mono" placeholder="64.01" data-testid="account-form-code-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">{t("account_name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-form-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">{t("account_type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="account-form-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map((v) => <SelectItem key={v} value={v}>{t(`acc_${v}`)}</SelectItem>)}</SelectContent>
              </Select></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" className="bg-blue-800 hover:bg-blue-700" data-testid="account-form-submit-button">{t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_account")} onConfirm={remove} />
    </div>
  );
};
